'use client'

import { useTranslation } from '@/hooks/client/use-translation'
import type { FilterOption, FilterOptions } from '@/lib/directory/build-filter-options'
import type { FacetAxis, FacetCounts } from '@/lib/directory/facet-counts'

import { ChoiceGroup } from './choice-group'
import { FeatureModeToggle } from './feature-mode-toggle'
import { purchaseLabel } from './purchase-label'

import { trackDirectory } from '../track-directory'
import { ActionType, type DirectoryAction, type DirectoryFilters, type FilterArrayKey } from '../use-directory-reducer'

interface FilterGroupsProps {
  filters: DirectoryFilters
  filterOptions: FilterOptions
  /** Live counts under the current filters (see countFacets). */
  counts: FacetCounts
  dispatch: React.Dispatch<DirectoryAction>
  /** Distinguishes checkbox ids between the rail and the sheet. */
  idPrefix: string
}

// The facets, in the order a shopper asks the questions: what kind, what shape,
// what for, whose, how much loss, then the long tails (features, how you buy,
// accessories) closed by default. Single-pick facets keep their radio
// semantics (picking another swaps; picking the same clears). Every option
// shows its live count, and an option that would leave nothing on screen is
// dropped from the group unless it is the one selected.
export const FilterGroups = ({ filters, filterOptions, counts, dispatch, idPrefix }: FilterGroupsProps) => {
  const { t } = useTranslation()
  const live = (axis: FacetAxis, options: FilterOption[], selected: string[]): FilterOption[] =>
    options
      .map((option) => ({ ...option, count: counts[axis].get(option.value) ?? 0 }))
      .filter((option) => option.count > 0 || selected.includes(option.value))
  const brandSelected = filters.brand ? [filters.brand] : []

  const toggle = (key: FilterArrayKey) => (value: string) => {
    trackDirectory('directory-filter', { axis: key, value, active: !filters[key].includes(value) })
    dispatch({ type: ActionType.ToggleFilter, key, value })
  }
  const radio = (key: FilterArrayKey) => (value: string) => {
    trackDirectory('directory-filter', { axis: key, value, active: !filters[key].includes(value) })
    dispatch({ type: ActionType.RadioFilter, key, value })
  }
  const setBrand = (value: string) => {
    trackDirectory('directory-filter', { axis: 'brand', value, active: filters.brand !== value })
    dispatch({ type: ActionType.SetBrand, value })
  }

  // Hardware first, then software; "Most Water/Dust Resistant" ride along as
  // hardware values (see build-filter-options).
  const hardwareValues = new Set(filterOptions.hardware.map((option) => option.value))
  const features = [
    ...live('hardware', filterOptions.hardware, filters.hardware),
    ...live('software', filterOptions.software, filters.software),
  ]
  const toggleFeature = (value: string) => (hardwareValues.has(value) ? toggle('hardware') : toggle('software'))(value)
  const channels = live('channels', filterOptions.channels, filters.channels).map((option) => ({
    ...option,
    label: purchaseLabel(option.value, t),
  }))

  return (
    <div>
      <ChoiceGroup
        id={`${idPrefix}-type`}
        title={t('directory.filter.type', 'Type')}
        options={live('classification', filterOptions.classification, filters.classification)}
        selected={filters.classification}
        mode="pills"
        onToggle={radio('classification')}
      />
      <ChoiceGroup
        id={`${idPrefix}-style`}
        title={t('directory.filter.style', 'Style')}
        options={live('style', filterOptions.style, filters.style)}
        selected={filters.style}
        mode="checks"
        onToggle={toggle('style')}
      />
      <ChoiceGroup
        id={`${idPrefix}-best-for`}
        title={t('directory.filter.bestOf', 'Best for')}
        options={live('tags', filterOptions.tags, filters.tags)}
        selected={filters.tags}
        mode="pills"
        onToggle={radio('tags')}
      />
      <ChoiceGroup
        id={`${idPrefix}-brand`}
        title={t('directory.filter.brand', 'Brand')}
        options={live('brand', filterOptions.brands, brandSelected)}
        selected={brandSelected}
        mode="checks"
        onToggle={setBrand}
        initialCount={6}
      />
      <ChoiceGroup
        id={`${idPrefix}-loss`}
        title={t('directory.filter.hearingLoss', 'Hearing loss')}
        options={live('loss', filterOptions.loss, filters.loss)}
        selected={filters.loss}
        mode="pills"
        onToggle={radio('loss')}
      />
      <ChoiceGroup
        id={`${idPrefix}-features`}
        title={t('directory.filter.features', 'Features')}
        options={features}
        selected={[...filters.hardware, ...filters.software]}
        mode="checks"
        onToggle={toggleFeature}
        defaultOpen={false}
        initialCount={8}
        headerControl={
          filters.hardware.length + filters.software.length > 0 ? (
            <FeatureModeToggle
              value={filters.featureMode}
              onChange={(value) => {
                trackDirectory('directory-filter', { axis: 'featureMode', value })
                dispatch({ type: ActionType.SetFeatureMode, value })
              }}
            />
          ) : undefined
        }
      />
      <ChoiceGroup
        id={`${idPrefix}-purchase`}
        title={t('directory.filter.purchase', 'How you buy')}
        options={channels}
        selected={filters.channels}
        mode="pills"
        onToggle={radio('channels')}
        defaultOpen={false}
      />
      <ChoiceGroup
        id={`${idPrefix}-accessories`}
        title={t('directory.filter.accessories', 'Accessories')}
        options={live('accessories', filterOptions.accessories, filters.accessories)}
        selected={filters.accessories}
        mode="checks"
        onToggle={toggle('accessories')}
        defaultOpen={false}
      />
    </div>
  )
}
